import { useEffect, useState } from "react";
import { Trash2, ArrowRight, Plus, Info, Users } from "lucide-react";
import ImportStudentsModal from "../../modals/ImportStudentsModal";
import { toast } from "react-toastify";
import useUserStore from "../../../stores/user";
import supabase from "../../../utils/supabase";
import { displayEmail } from "../../../utils/kidLogin";

interface ClassOption {
  id: string;
  name: string;
  semester: string;
}

interface EnrolledStudent {
  userId: string;
  firstname: string;
  lastname: string;
  email: string;
  classId: string;
}

interface MoveStudentState {
  student: EnrolledStudent;
  targetClassId: string;
}

export default function Roster() {
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const [students, setStudents] = useState<EnrolledStudent[]>([]);
  const [isClassesLoading, setIsClassesLoading] = useState(true);
  const [isStudentsLoading, setIsStudentsLoading] = useState(false);
  const [isDistrictLoading, setIsDistrictLoading] = useState(true);
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [movingStudent, setMovingStudent] = useState<MoveStudentState | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const { profile } = useUserStore();
  const [district, setDistrict] = useState<{ id: string; name: string } | null>(null);

  async function fetchDistrict() {
    if (!profile?.district) {
      setIsDistrictLoading(false);
      return;
    }
    setIsDistrictLoading(true);
    const { data, error } = await supabase
      .from("districts")
      .select("id, name")
      .eq("id", profile.district)
      .maybeSingle();
    if (error) console.error("Failed to fetch district:", error);
    setDistrict(data);
    setIsDistrictLoading(false);
  }

  async function fetchDistrictClasses() {
    if (!profile?.district) {
      setClasses([]);
      setIsClassesLoading(false);
      return;
    }
    setIsClassesLoading(true);

    const { data: schools } = await supabase
      .from("schools")
      .select("id")
      .eq("district_id", profile.district);

    const schoolIds = (schools ?? []).map((s) => s.id);

    let teacherIds: string[] = [];

    if (schoolIds.length) {
      const { data: teachers } = await supabase
        .from("profiles")
        .select("id")
        .in("school", schoolIds)
        .eq("role", "teacher");
      teacherIds = (teachers ?? []).map((t) => t.id);
    }

    // Include admin's own user_id
    if (profile.id) teacherIds.push(profile.id);

    if (!teacherIds.length) {
      setClasses([]);
      setIsClassesLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("classes")
      .select("id, name, semester")
      .in("user_id", teacherIds)
      .order("name", { ascending: true });

    if (error) {
      console.error("Failed to fetch classes:", error);
      setClasses([]);
      setIsClassesLoading(false);
      return;
    }

    setClasses(data ?? []);
    if (data?.length && !selectedClassId) {
      setSelectedClassId(data[0].id);
    }
    setIsClassesLoading(false);
  }

  async function fetchStudents(classId: string) {
    if (!classId) {
      setStudents([]);
      return;
    }
    setIsStudentsLoading(true);

    const { data: enrollments, error: enrollErr } = await supabase
      .from("class_students")
      .select("user_id, class_id")
      .eq("class_id", classId);

    if (enrollErr) {
      console.error("Failed to fetch enrollments:", enrollErr);
      setStudents([]);
      setIsStudentsLoading(false);
      return;
    }

    if (!enrollments?.length) {
      setStudents([]);
      setIsStudentsLoading(false);
      return;
    }

    const userIds = enrollments.map((e) => e.user_id);

    const { data: profilesData, error: profilesErr } = await supabase
      .from("profiles")
      .select("id, firstname, lastname, email")
      .in("id", userIds);

    if (profilesErr) {
      console.error("Failed to fetch profiles:", profilesErr);
      setStudents([]);
      setIsStudentsLoading(false);
      return;
    }

    const profileMap: Record<string, { firstname: string; lastname: string; email: string }> = {};
    (profilesData ?? []).forEach((p) => {
      profileMap[p.id] = { firstname: p.firstname, lastname: p.lastname, email: p.email };
    });

    const mapped: EnrolledStudent[] = enrollments.map((e) => ({
      userId: e.user_id,
      classId: e.class_id,
      firstname: profileMap[e.user_id]?.firstname ?? "",
      lastname: profileMap[e.user_id]?.lastname ?? "",
      email: profileMap[e.user_id]?.email ?? "",
    }));

    setStudents(mapped.sort((a, b) => a.lastname.localeCompare(b.lastname)));
    setIsStudentsLoading(false);
  }

  useEffect(() => {
    fetchDistrict();
    fetchDistrictClasses();
  }, [profile]);

  useEffect(() => {
    if (selectedClassId) {
      fetchStudents(selectedClassId);
    }
  }, [selectedClassId]);

  const handleRemoveStudent = async (student: EnrolledStudent) => {
    setIsRemoving(student.userId);
    const { error } = await supabase
      .from("class_students")
      .delete()
      .eq("user_id", student.userId)
      .eq("class_id", student.classId);

    if (error) {
      toast.error("Failed to remove student: " + error.message);
    } else {
      toast.success(`${student.firstname} ${student.lastname} removed from class`);
      fetchStudents(selectedClassId);
    }
    setIsRemoving(null);
  };

  const handleMoveStudent = async () => {
    if (!movingStudent) return;
    const { student, targetClassId } = movingStudent;
    if (!targetClassId || targetClassId === student.classId) {
      toast.error("Please select a different class to move the student to");
      return;
    }
    setIsMoving(true);

    // Remove from current class
    const { error: removeErr } = await supabase
      .from("class_students")
      .delete()
      .eq("user_id", student.userId)
      .eq("class_id", student.classId);

    if (removeErr) {
      toast.error("Failed to move student: " + removeErr.message);
      setIsMoving(false);
      return;
    }

    // Add to new class
    const { error: addErr } = await supabase
      .from("class_students")
      .insert([{ user_id: student.userId, class_id: targetClassId }]);

    if (addErr) {
      toast.error("Failed to enroll in new class: " + addErr.message);
      setIsMoving(false);
      return;
    }

    toast.success(`${student.firstname} ${student.lastname} moved successfully`);
    setMovingStudent(null);
    fetchStudents(selectedClassId);
    setIsMoving(false);
  };

  const selectedClassName = classes.find((c) => c.id === selectedClassId)?.name ?? "";

  return (
    <div>
      <ImportStudentsModal
        isOpen={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        selectedClassId={selectedClassId || undefined}
        onComplete={(result) => {
          if (!result.error) {
            fetchStudents(selectedClassId);
          }
        }}
      />

      {/* Move student modal */}
      {movingStudent && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
          <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-6">
            <h3 className="text-lg font-semibold mb-4">Move Student</h3>
            <p className="text-sm text-gray-600 mb-4">
              Moving{" "}
              <strong>
                {movingStudent.student.firstname} {movingStudent.student.lastname}
              </strong>{" "}
              from <strong>{selectedClassName}</strong> to:
            </p>
            <select
              className="w-full border p-2 rounded-lg mb-6"
              value={movingStudent.targetClassId}
              onChange={(e) =>
                setMovingStudent({ ...movingStudent, targetClassId: e.target.value })
              }
            >
              <option value="">— Select destination class —</option>
              {classes
                .filter((c) => c.id !== selectedClassId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.semester})
                  </option>
                ))}
            </select>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setMovingStudent(null)}
                disabled={isMoving}
                className="btn btn-small btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleMoveStudent}
                disabled={isMoving || !movingStudent.targetClassId}
                className="btn btn-small"
              >
                {isMoving ? "Moving..." : "Move Student"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b shadow-sm p-6 flex justify-between gap-5 items-start lg:items-center flex-col lg:flex-row">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Roster</h1>
          <p className="text-sm text-gray-600 mt-1">
            District:{" "}
            {isDistrictLoading
              ? "Loading..."
              : district?.name || "Not assigned"}
          </p>
          {!district?.name && !isDistrictLoading && (
            <p className="text-sm text-yellow-600 mt-1 flex items-center gap-1">
              <Info className="w-4 h-4" />
              Please contact Project Explore <b>Super Admin</b> to get assigned
              to a district.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsAddOpen(true)}
            disabled={!selectedClassId || !district?.name}
            className="btn btn-small"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Students
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto py-6 px-4 bg-gray-50">
        {/* Class picker */}
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Class
              </label>
              {isClassesLoading ? (
                <div className="text-sm text-gray-400 italic">Loading classes...</div>
              ) : (
                <select
                  className="w-full p-2 border rounded-lg"
                  value={selectedClassId}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                  disabled={!district?.name}
                >
                  <option value="">— Select a class —</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.semester})
                    </option>
                  ))}
                </select>
              )}
            </div>
            {selectedClassId && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Users className="w-4 h-4" />
                {isStudentsLoading ? "..." : students.length} student
                {students.length !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>

        {/* Student table */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {!selectedClassId ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-center text-sm text-gray-400 py-6 italic"
                    >
                      Select a class above to view its roster.
                    </td>
                  </tr>
                ) : isStudentsLoading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-center text-sm text-gray-500 py-6 italic"
                    >
                      Loading students...
                    </td>
                  </tr>
                ) : students.length === 0 ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="text-center text-sm text-gray-500 py-6"
                    >
                      No students enrolled in this class.
                    </td>
                  </tr>
                ) : (
                  students.map((student) => (
                    <tr key={student.userId}>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">
                          {student.firstname} {student.lastname}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-500">{displayEmail(student.email, "No email")}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() =>
                              setMovingStudent({ student, targetClassId: "" })
                            }
                            className="text-indigo-400 hover:text-indigo-600 transition-colors"
                            title="Move to another class"
                            disabled={classes.length <= 1}
                          >
                            <ArrowRight className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleRemoveStudent(student)}
                            disabled={isRemoving === student.userId}
                            className="text-red-400 hover:text-red-600 transition-colors"
                            title="Remove from class"
                          >
                            {isRemoving === student.userId ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400" />
                            ) : (
                              <Trash2 className="h-5 w-5" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
